import { usePollingInterval } from 'src/hooks/usePollingInterval'
import { makeStyles } from '@material-ui/core'
import { formatName, getDealStageOptions, formatCurrency, getQuoteTags } from '@paintscout/util/builder'
import {
  BoardView as UIBoardView,
  EstimatorAvatar,
  Tooltip,
  Typography,
  useClientOptions,
  useUser,
  useUsers,
  useIsMobile
} from '@paintscout/ui'
import type { OptionsDocument, PipelineStage } from 'paintscout'
import {
  forwardRef,
  default as React,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import HistoryIcon from '@material-ui/icons/History'
import { useTheme } from '@material-ui/core/styles'
import DealSearchBar from 'src/components/common/DealSearchBar'
import { useLocation, Link } from 'react-router-dom'
import { jsonToLucene } from 'json-to-lucene'
import moment from 'moment'
import type { DropResult } from '@hello-pangea/dnd'
import {
  useDealSummaryQuery,
  useSearchActivitiesQuery,
  useUpdateDealStageMutation,
  useUpdateUserMutation,
  SearchDealsByStageDocument
} from '@paintscout/api'
import type {
  Deal,
  DealSummaryResponse,
  SearchDealsByStageQuery,
  SearchDealsByStageQueryVariables
} from '@paintscout/api'
import { useApolloClient } from '@apollo/react-hooks'
import TagIcon from '@material-ui/icons/LocalOffer'
import type { Theme } from '@material-ui/core/styles'
import { QuoteTagTooltip } from 'src/components/common/QuoteTagIcons'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import type { DealUrlQuery } from 'src/components/common/DealSearchBar'
import type { BoardViewData } from '@paintscout/ui'
import { getFiltersFromLocation } from 'src/components/common/QuoteSearchBar'
import isEmpty from 'lodash/isEmpty'
import { handlePipelineQuery } from './ListView'

const POLL_INTERVAL_MS = 60000

// Valid sort fields for BoardView. 'stage' is sortable in ListView but meaningless in BoardView
// (all cards in a column share the same stage) and causes an ES error since stage is an object field.
const VALID_BOARD_SORT_FIELDS = new Set(['name', 'value', 'enteredStage', 'lead_source', 'date', 'owner'])

function getBackendSort(sort: string): string[] {
  const isDesc = sort.startsWith('-')
  const field = sort.replace('-', '')
  const prefix = isDesc ? '-' : ''

  if (!VALID_BOARD_SORT_FIELDS.has(field)) {
    return ['name', '-updated']
  }

  // Map frontend field names to ES field names
  const fieldMap: Record<string, string> = {
    date: 'created',
    owner: 'owner.firstName'
  }
  const esField = fieldMap[field] || field

  // enteredStage has inverted semantics: "ascending time in stage" = descending timestamp
  if (field === 'enteredStage') {
    return [isDesc ? esField : `-${esField}`, '-updated']
  }

  return [`${prefix}${esField}`, '-updated']
}

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    contentsWrapper: {
      '& a': {
        textDecoration: 'none',
        color: 'inherit'
      }
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: theme.spacing(2),
      padding: '0 16px',
      flexWrap: 'wrap',
      gap: theme.spacing(1)
    },
    summaryTotals: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(3),
      flexWrap: 'wrap'
    }
  }),
  { name: 'BoardView' }
)

export interface BoardViewProps {
  pipeline: string
}

export function BoardView(props: BoardViewProps) {
  const { pipeline } = props
  const { options } = useClientOptions()
  const location = useLocation<DealUrlQuery>()
  const urlParams = getUrlQuery<DealUrlQuery>({ location })
  const stageOptions = getDealStageOptions({ options, pipeline })
  const rawSort = urlParams.sort || 'name'
  const rawSortField = rawSort.replace('-', '')
  const sort = VALID_BOARD_SORT_FIELDS.has(rawSortField) ? rawSort : 'name'
  const classes = useStyles()

  useEffect(() => {
    if (!VALID_BOARD_SORT_FIELDS.has(rawSortField)) {
      setUrlQuery({ location, params: { sort: 'name' } })
    }
  }, [rawSortField])

  const [data, setData] = useState<BoardViewData>({})
  const [filters, setFilters] = useState(() => getFiltersFromLocation(location, true))
  const [queryText, setQueryText] = useState('')
  const query = useMemo(
    () =>
      handlePipelineQuery({
        pipeline,
        filters,
        query: queryText
      }),
    [filters, pipeline, queryText]
  )
  const [loadingStages, setLoadingStages] = useState<string[]>([])
  const [fetchingMore, setFetchingMore] = useState<string[]>([])
  const [hasMoreStages, setHasMoreStages] = useState<string[]>([])
  const [hasErrors, setHasErrors] = useState<string[]>([])
  const stageQueryRefs = useRef<Record<string, { fetchMore: () => void; hasMore: boolean }>>({})

  const { preferences, user } = useUser()
  const notificationStorageKey = useMemo(
    () => (user ? `ps.dealsBoard.newLeads:lastSeen:${user.user_id}` : null),
    [user?.user_id]
  )
  const storedLastSeen = useMemo(() => {
    if (!notificationStorageKey || typeof window === 'undefined') {
      return 0
    }
    try {
      const storedValue = window.localStorage.getItem(notificationStorageKey)
      const parsed = Number(storedValue)
      return Number.isFinite(parsed) ? parsed : 0
    } catch {
      return 0
    }
  }, [notificationStorageKey])
  const [lastSeenTimestamp, setLastSeenTimestamp] = useState(() =>
    Math.max(preferences?.lastSeenDealBoardLeadFormSubmission ?? 0, storedLastSeen)
  )
  const [updateUser] = useUpdateUserMutation()
  const [updateDealStage] = useUpdateDealStageMutation({
    fetchPolicy: 'no-cache' // to avoid caching the updated deal which would refire the rawData useEffect
    // TODO: avoiding cache is maybe the wrong approach here, if you move a deal, leave kanban and come back, it will not be in the new stage for a sec
  })

  const shouldQueryLeads = Boolean(user) && pipeline === 'sales-pipeline'
  const leadsQueryString = useMemo(() => {
    if (!shouldQueryLeads) {
      return ''
    }
    return jsonToLucene({
      filters: [
        {
          id: 'details.activityType',
          type: 'equal',
          value: 'lead-form-submission'
        },
        {
          id: 'details.timestamp',
          type: 'greaterThan',
          value: lastSeenTimestamp
        }
      ]
    })
  }, [lastSeenTimestamp, shouldQueryLeads])

  const { data: leadData } = useSearchActivitiesQuery({
    skip: !shouldQueryLeads,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    pollInterval: usePollingInterval(POLL_INTERVAL_MS),
    variables: {
      query: leadsQueryString,
      limit: 1
    }
  })

  const totalNewLeadCount = leadData?.searchActivities?.total_rows ?? 0
  const latestNotificationTimestamp = Number(leadData?.searchActivities?.activities?.[0]?.details?.timestamp ?? 0)
  const [pendingNewLeadCount, setPendingNewLeadCount] = useState(0)
  const [suppressPending, setSuppressPending] = useState(false)

  useEffect(() => {
    const prefTimestamp = preferences?.lastSeenDealBoardLeadFormSubmission ?? 0
    if (prefTimestamp) {
      setLastSeenTimestamp((current) => Math.max(current, prefTimestamp))
    }
  }, [preferences?.lastSeenDealBoardLeadFormSubmission])

  useEffect(() => {
    if (!notificationStorageKey || typeof window === 'undefined') {
      return
    }
    try {
      window.localStorage.setItem(notificationStorageKey, String(lastSeenTimestamp))
    } catch {
      // ignore
    }
  }, [lastSeenTimestamp, notificationStorageKey])

  useEffect(() => {
    if (storedLastSeen <= lastSeenTimestamp || storedLastSeen <= 0) {
      return
    }
    setLastSeenTimestamp(storedLastSeen)
  }, [lastSeenTimestamp, storedLastSeen])

  const markLeadsAsSeen = useCallback(async () => {
    if (totalNewLeadCount === 0) {
      return
    }

    const timestamp = Math.max(latestNotificationTimestamp, Date.now())
    setLastSeenTimestamp((current) => Math.max(current, timestamp))
    if (notificationStorageKey && typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(notificationStorageKey, String(timestamp))
      } catch {
        // ignore
      }
    }

    if (!user) {
      return
    }

    try {
      await updateUser({
        variables: {
          input: {
            user_id: user.user_id,
            preferences: {
              ...(preferences ?? {}),
              lastSeenDealBoardLeadFormSubmission: timestamp
            }
          }
        }
      })
    } catch (error) {
      console.error('Failed to persist deal board lead preferences', error)
    }
  }, [latestNotificationTimestamp, preferences, totalNewLeadCount, updateUser, user])

  useEffect(() => {
    if (!shouldQueryLeads) {
      setPendingNewLeadCount(0)
      return
    }

    if (suppressPending) {
      return
    }
    if (totalNewLeadCount > pendingNewLeadCount) {
      setPendingNewLeadCount(totalNewLeadCount)
    } else if (totalNewLeadCount === 0 && pendingNewLeadCount > 0) {
      setPendingNewLeadCount(0)
    }
  }, [pendingNewLeadCount, shouldQueryLeads, suppressPending, totalNewLeadCount])

  const handleMarkNewLeadsSeen = useCallback(() => {
    if (pendingNewLeadCount === 0) {
      return
    }
    setPendingNewLeadCount(0)
    setSuppressPending(true)
    markLeadsAsSeen().finally(() => {
      setSuppressPending(false)
    })
  }, [markLeadsAsSeen, pendingNewLeadCount])

  const handleData = useCallback(
    (stageValue: string, deals: Deal[], aggregates: DealSummaryResponse, hasMore: boolean, offset: number) => {
      setHasMoreStages((prev) => {
        if (hasMore && !prev.includes(stageValue)) {
          return [...prev, stageValue]
        } else if (!hasMore && prev.includes(stageValue)) {
          return prev.filter((s) => s !== stageValue)
        }
        return prev
      })
      setLoadingStages((prev) => prev.filter((s) => s !== stageValue))
      setFetchingMore((prev) => prev.filter((s) => s !== stageValue))
      setHasErrors((prev) => prev.filter((s) => s !== stageValue))
      setData((prev) => {
        // For initial load, replace data. For fetchMore, append.
        const isInitialLoad = offset === 0
        let stageData
        if (isInitialLoad) {
          stageData = { deals, aggregates }
        } else {
          const existingDeals = prev[stageValue].deals || []
          const existingIds = new Set(existingDeals.map((d) => d._id))
          // Deduplicate: skip any newly loaded deals that already exist
          // (e.g. a deal moved here via drag-and-drop before this page loaded)
          const newDeals = deals.filter((d) => !existingIds.has(d._id))
          stageData = { deals: [...existingDeals, ...newDeals], aggregates }
        }

        const newData = { ...prev, [stageValue]: stageData }
        // Re-sort after appending so that any previously moved deals
        // remain in the correct position relative to newly loaded data.
        return handleSortData({ data: newData, sort }) || newData
      })
    },
    [sort]
  )

  const handleError = useCallback((err: Error, stageValue: string) => {
    console.log(`Stage ${stageValue} query error:`, err)
    setHasErrors((prev) => (prev.includes(stageValue) ? prev : [...prev, stageValue]))
    setLoadingStages((prev) => prev.filter((s) => s !== stageValue))
    setFetchingMore((prev) => prev.filter((s) => s !== stageValue))
  }, [])

  const handleLoadingChange = useCallback((stageValue: string, isLoading: boolean, isFetchingMore = false) => {
    if (isFetchingMore) {
      setFetchingMore((prev) => (isLoading ? [...prev, stageValue] : prev.filter((s) => s !== stageValue)))
    } else {
      setLoadingStages((prev) => (isLoading ? [...prev, stageValue] : prev.filter((s) => s !== stageValue)))
    }
  }, [])

  const handleSearch = useCallback((text, filters) => {
    setQueryText(text ?? '')
    setFilters(filters)
    // StageQuery components will automatically re-run due to useEffect
  }, [])

  const handleFetchMore = useCallback(
    (stageValue: string) => {
      if (stageQueryRefs.current[stageValue]?.hasMore && !fetchingMore.includes(stageValue)) {
        stageQueryRefs.current[stageValue].fetchMore()
      }
    },
    [fetchingMore]
  )

  const {
    loading: _dealSummaryFetching,
    error: _dealSummaryError,
    data: dealSummaryData
  } = useDealSummaryQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      query,
      pipeline
    }
  })

  const loading = loadingStages.length > 0

  return (
    <>
      <DealSearchBar
        view={'board'}
        loading={loading}
        filters={filters}
        pipeline={pipeline}
        hasStage={false}
        onSearch={handleSearch}
      />

      <div className={classes.summaryRow}>
        <div className={classes.summaryTotals}>
          <div>{`Total Deals: ${dealSummaryData?.dealSummary?.totalDeals ?? 0}`}</div>
          <div>{`Total Value: ${formatCurrency({ value: dealSummaryData?.dealSummary?.totalDealValue ?? 0, options })}`}</div>
        </div>
      </div>

      {stageOptions.map((stage) => (
        <StageQueryWrapper
          key={stage.key}
          ref={(ref) => {
            if (ref) {
              stageQueryRefs.current[stage.key] = ref
            } else {
              delete stageQueryRefs.current[stage.key]
            }
          }}
          stage={stage}
          query={query}
          sort={sort}
          onData={handleData}
          onError={(error) => handleError(error, stage.key)}
          onLoadingChange={(_, isLoading, isFetchingMore) => handleLoadingChange(stage.key, isLoading, isFetchingMore)}
        />
      ))}
      <UIBoardView
        keyField={'_id'}
        columns={stageOptions}
        contentsComponent={BoardViewContents}
        data={data}
        onDragEnd={handleOnDragEnd}
        pipeline={pipeline}
        loadingStages={loadingStages}
        hasMoreStages={hasMoreStages}
        hasErrors={hasErrors}
        fetchingMore={fetchingMore}
        onLoadMore={handleFetchMore}
        newLeadCount={pendingNewLeadCount}
        onMarkNewLeadsSeen={handleMarkNewLeadsSeen}
      />
    </>
  )

  async function handleOnDragEnd(dropResult: DropResult, updatedData: BoardViewData) {
    // state has already been locally updated with changes
    // now we just need to do an optimistic async update via api
    const { source, destination, draggableId } = dropResult

    const changedDeal = updatedData[destination.droppableId].deals.find((deal) => deal._id === draggableId)
    const { enteredStage, ...requestDeal } = changedDeal

    changedDeal.enteredStage = Date.now()

    setData(handleSortData({ data: updatedData, sort }))

    updateDealStage({
      variables: {
        dealId: requestDeal._id,
        stage: destination.droppableId,
        query
      }
    })
      .then((res) => {
        // update state with the response
        const updatedDeal = res.data.updateDealStage.deal
        const updatedSourceAggregations = res.data.updateDealStage.sourceAggregations
        const updatedDestAggregations = res.data.updateDealStage.destimationAggregations
        setData((prevData) =>
          handleSortData({
            data: {
              ...prevData,
              [source.droppableId]: {
                deals: prevData[source.droppableId].deals.filter((deal) => deal._id !== draggableId),
                aggregates: updatedSourceAggregations
              },
              [destination.droppableId]: {
                deals: prevData[destination.droppableId].deals.map((deal) => {
                  if (deal._id === draggableId) {
                    return {
                      ...deal,
                      ...updatedDeal
                    }
                  }
                  return deal
                }),
                aggregates: updatedDestAggregations
              }
            },
            sort
          })
        )
      })
      .catch((err) => {
        console.log(err)
        // revert the optimistic update
        setData((prevData) =>
          handleSortData({
            data: {
              ...prevData,
              [destination.droppableId]: {
                deals: prevData[destination.droppableId].deals.filter((deal) => deal._id !== draggableId),
                aggregates: prevData[destination.droppableId].aggregates
              },
              [source.droppableId]: {
                deals: [
                  ...prevData[source.droppableId].deals,
                  { ...prevData[destination.droppableId].deals.find((deal) => deal._id === draggableId), enteredStage }
                ],
                aggregates: prevData[source.droppableId].aggregates
              }
            },
            sort
          })
        )
      })
  }
}

// Wrapper component to expose StageQuery methods via ref
interface StageQueryWrapperProps {
  stage: PipelineStage
  query: string
  sort: string
  onData: (stageValue: string, deals: Deal[], aggregates: DealSummaryResponse, hasMore: boolean, offset: number) => void
  onError: (error: Error, stageValue: string) => void
  onLoadingChange: (stageValue: string, isLoading: boolean, isFetchingMore?: boolean) => void
}

interface StageQueryMethods {
  fetchMore: () => void
  hasMore: boolean
}

const StageQueryWrapper = forwardRef<any, StageQueryWrapperProps>(
  ({ stage, query, sort, onData, onError, onLoadingChange }, ref) => {
    const stageQuery = StageQuery({
      stage,
      query,
      sort,
      onData,
      onError,
      onLoadingChange
    })

    useImperativeHandle(
      ref,
      (): StageQueryMethods => ({
        fetchMore: stageQuery.fetchMore,
        hasMore: stageQuery.hasMore
      }),
      [stageQuery]
    )

    return null
  }
)
StageQueryWrapper.displayName = 'StageQueryWrapper'

function StageQuery({
  stage,
  query,
  sort,
  onData,
  onError,
  onLoadingChange,
  pageSize = 20
}: {
  stage: PipelineStage
  sort: string
  query: string
  onData: (stageValue: string, deals: Deal[], aggregates: DealSummaryResponse, hasMore: boolean, offset: number) => void
  onError: (error: Error, stageValue: string) => void
  onLoadingChange: (stageValue: string, isLoading: boolean, isFetchingMore?: boolean) => void
  pageSize?: number
}) {
  const client = useApolloClient()
  const offsetRef = useRef(0)
  const requestGenRef = useRef(0)
  const [bookmark, setBookmark] = useState<string | undefined>(undefined)
  const [hasMore, setHasMore] = useState(true)

  // Callback refs so async handlers always call the latest prop functions
  // without needing them in effect/callback dependency arrays
  const onDataRef = useRef(onData)
  const onErrorRef = useRef(onError)
  const onLoadingChangeRef = useRef(onLoadingChange)
  onDataRef.current = onData
  onErrorRef.current = onError
  onLoadingChangeRef.current = onLoadingChange

  const runQuery = useCallback(
    (variables: SearchDealsByStageQueryVariables, gen: number, startOffset: number, isFetchingMore: boolean) => {
      client
        .query<SearchDealsByStageQuery, SearchDealsByStageQueryVariables>({
          query: SearchDealsByStageDocument,
          fetchPolicy: 'network-only',
          variables
        })
        .then((result) => {
          if (gen !== requestGenRef.current) return
          if (result.errors?.length) {
            onErrorRef.current(new Error(result.errors[0].message), stage.key)
            onLoadingChangeRef.current(stage.key, false, isFetchingMore)
            return
          }
          const resData = result.data
          const deals = resData?.searchDealsByStage?.deals || []
          const totalRows = resData?.searchDealsByStage?.total_rows ?? 0
          const aggregates = resData?.searchDealsByStage?.aggregations ?? { totalDeals: 0, totalDealValue: 0 }
          const newHasMore = startOffset + deals.length < totalRows
          offsetRef.current = startOffset + deals.length
          setBookmark(resData?.searchDealsByStage?.bookmark)
          onDataRef.current(stage.key, deals, aggregates, newHasMore, startOffset)
          setHasMore(newHasMore)
          onLoadingChangeRef.current(stage.key, false, isFetchingMore)
        })
        .catch((error) => {
          if (gen !== requestGenRef.current) return
          onErrorRef.current(error, stage.key)
          onLoadingChangeRef.current(stage.key, false, isFetchingMore)
        })
    },
    [client, stage]
  )

  // Initial load
  useEffect(() => {
    setHasMore(true)
    offsetRef.current = 0
    const gen = ++requestGenRef.current
    onLoadingChangeRef.current(stage.key, true, false)
    runQuery({ query, stage: stage.key, limit: pageSize, sort: getBackendSort(sort) }, gen, 0, false)
  }, [query, sort, stage, pageSize, runQuery])

  // Function to fetch more data
  const fetchMore = useCallback(() => {
    const gen = ++requestGenRef.current
    const startOffset = offsetRef.current
    onLoadingChangeRef.current(stage.key, true, true)
    runQuery({ query, stage: stage.key, bookmark, limit: pageSize, sort: getBackendSort(sort) }, gen, startOffset, true)
  }, [pageSize, stage, query, sort, bookmark, runQuery])

  return { fetchMore, hasMore }
}

function BoardViewContents({ data }: { data: Deal }) {
  const classes = useStyles()
  const theme = useTheme()
  const { options } = useClientOptions()
  const isMobile = useIsMobile()
  const { userPictures } = useUsers()

  const now = Date.now()

  const getTimeDifference = (enteredStage: number): string => {
    const duration = moment.duration(moment(now).diff(moment(enteredStage)))
    // If enteredStage is null then we don't have a deal.history, so we can assume it's very recent
    if (duration.asHours() < 1 || !enteredStage) {
      return `<1hr`
    } else if (duration.asDays() < 1) {
      return `${Math.floor(duration.asHours())}hr`
    } else {
      return `${Math.floor(duration.asDays())}d`
    }
  }

  return (
    <div className={classes.contentsWrapper}>
      <Link to={`/deals/edit/${data._id}`}>
        <Typography variant={'body1'} style={{ fontWeight: 500 }}>
          {data.name}
        </Typography>
        <Typography component={'div'} variant={'caption'}>
          {formatName(data.contact)}
        </Typography>
        <Typography component={'div'} variant={'caption'}>
          {formatCurrency({ value: data.value, options })}
        </Typography>
        {/* <Typography component={'div'} variant={'caption'}>
        {moment(data.created).format('MM/DD/YYYY')}
        </Typography> */}
        {/* <Typography component={'div'} variant={'caption'}>
        {data.quoteType}
        </Typography> */}
        <div
          style={{
            borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P94}`,
            marginRight: -8,
            marginLeft: -8,
            marginTop: 24
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 4
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ overflow: 'hidden', height: 28, width: 28 }}>
              {data.owner && <EstimatorAvatar owner={data.owner} userPictures={userPictures} disableTooltip />}
            </div>

            <DealTags tags={data.tags} options={options} />
          </div>
          <div style={{ overflow: 'hidden' }}>
            <Tooltip disabled={isMobile} content={'Time in stage'} icon={<></>}>
              <Typography variant={'caption'} style={{ display: 'flex', alignItems: 'center' }}>
                <HistoryIcon style={{ fontSize: '1rem', marginRight: 2 }} />
                <div style={{ lineHeight: 0 }}>{getTimeDifference(data.enteredStage)}</div>
              </Typography>
            </Tooltip>
          </div>
        </div>
      </Link>
    </div>
  )
}

function DealTags(args: { tags: string[]; options: OptionsDocument }) {
  const { tags, options } = args
  if (!tags || tags.length === 0) return null
  const tagOptions = getQuoteTags({ options })
  const fullTags = tagOptions.filter((tagOption) => tags.includes(tagOption.name))

  if (isEmpty(fullTags)) return null

  return (
    <div style={{ marginTop: 6, marginLeft: 4 }}>
      <QuoteTagTooltip tags={fullTags}>
        <TagIcon style={{ width: 16, height: 16 }} />
      </QuoteTagTooltip>
    </div>
  )
}

function handleSortData(args: { data: BoardViewData; sort: string }) {
  const { data, sort } = args
  if (!data) return null
  if (Object.keys(data).length === 0) return data
  const isSortDescending = sort.startsWith('-')
  const sortBy = sort.replace('-', '')

  const sortedData = Object.entries(data).reduce((acc, [key, { deals, aggregates }]) => {
    const sortedDeals = [...deals].sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name)
      } else if (sortBy === 'owner') {
        return a.owner?.firstName.localeCompare(b.owner?.firstName)
      } else if (sortBy === 'value') {
        return a.value - b.value
      } else if (sortBy === 'date') {
        return a.created - b.created
      } else if (sortBy === 'enteredStage') {
        return b.enteredStage - a.enteredStage
      } else if (sortBy === 'lead_source') {
        return (a.lead_source || '').localeCompare(b.lead_source || '')
      }
      return 0
    })

    acc[key] = {
      deals: sortedDeals,
      aggregates
    }
    return acc
  }, {} as BoardViewData)

  if (isSortDescending) {
    Object.keys(sortedData).forEach((key) => {
      sortedData[key].deals = sortedData[key].deals.reverse()
    })
  }

  return sortedData
}
